const DEFAULT_DESCRIPTION =
  "Tell us what campground you're looking for. We'll check the reservation website every 1-5 minutes and notify you if a spot opens up.";

// Search engines truncate snippets around ~160 chars. Trim to the last whole
// word (+ ellipsis) so we never emit a mid-word cut — applied centrally here so
// callers pass full copy and never re-implement the truncation.
export const META_DESCRIPTION_MAX = 160;

export function truncateDescription(text, max = META_DESCRIPTION_MAX) {
  if (!text || text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, "") + "...";
}

export function getSeoMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  url,
  image,
  type = "website",
  publishedTime,
  modifiedTime,
  tags,
}) {
  const desc = truncateDescription(description);
  const meta = [
    { title },
    { name: "description", content: desc },
    { property: "og:type", content: type },
    { property: "og:title", content: title },
    { property: "og:description", content: desc },
    { property: "og:site_name", content: "Schnerp" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: desc },
  ];

  if (url) {
    meta.push({ tagName: "link", rel: "canonical", href: url });
    meta.push({ property: "og:url", content: url });
  }

  if (image) {
    // og:image and twitter:image must be absolute: share-card scrapers don't
    // resolve a site-relative path like a frontmatter "/blog/<slug>.jpg".
    const absoluteImage = url ? absoluteUrl(image, url) : image;
    meta.push({ property: "og:image", content: absoluteImage });
    meta.push({ name: "twitter:image", content: absoluteImage });
    if (OWN_OG_IMAGES.some((path) => absoluteImage.endsWith(path))) {
      // Declaring the size lets WhatsApp and Facebook draw the large card on
      // a link's first share, before they have fetched the image itself.
      meta.push({ property: "og:image:width", content: "1200" });
      meta.push({ property: "og:image:height", content: "630" });
      meta.push({
        property: "og:image:alt",
        content: 'A phone showing "A site opened! Book now" over a campsite',
      });
    }
  }

  if (type === "article") {
    // Lets Google show the hero at full size in results and Discover.
    meta.push({ name: "robots", content: "max-image-preview:large" });
  }

  if (type === "article" && publishedTime) {
    meta.push({
      property: "article:published_time",
      content: publishedTime,
    });
  }

  if (type === "article" && modifiedTime) {
    meta.push({
      property: "article:modified_time",
      content: modifiedTime,
    });
  }

  if (type === "article" && tags?.length) {
    tags.forEach((tag) => {
      meta.push({ property: "article:tag", content: tag });
    });
  }

  return meta;
}

export function absoluteUrl(pathOrUrl, base) {
  return new URL(pathOrUrl, base).href;
}

export function getBaseUrl(matches) {
  return matches?.[0]?.loaderData?.env?.BASE_URL || "https://schnerp.com";
}

// Schnerp's own link-preview images, 1200 x 630: a phone showing "A site
// opened! Book now" over a campsite, with the domain. New Zealand pages use
// the New Zealand image; every other page, Australia's included, uses the
// general one.
const OG_IMAGE_BY_COUNTRY = {
  nz: "/og/schnerp-og-new-zealand.png",
};
const OG_IMAGE_GENERAL = "/og/schnerp-og-general.png";
const OWN_OG_IMAGES = [OG_IMAGE_GENERAL, ...Object.values(OG_IMAGE_BY_COUNTRY)];

export function getDefaultOgImage(baseUrl, country) {
  return `${baseUrl}${OG_IMAGE_BY_COUNTRY[country] ?? OG_IMAGE_GENERAL}`;
}
